import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, CalendarIcon, DollarSign } from "lucide-react";
import { format } from "date-fns";

// HF-Apply-10 (2026-06-24) — loan-type selector has been REMOVED from
// this step. It's now Step 2's responsibility (StepLoanType.jsx, which
// fetches from /api/loan-types and reflects the live 11-type list).
//
// Previously Step 7 had its OWN hardcoded 7-type dropdown that didn't
// match Step 2's 11-type list. Borrowers who scrolled into Step 7 would
// inadvertently OVERWRITE their Step 2 selection with a legacy id like
// "commercial_property_loan" or "land_bank_land_loan". On submit,
// normalize_loan_type_id mapped these back to current ids, but in some
// cases (e.g. "other") the mapping yielded a fallback that didn't match
// the seeded checklist template — resulting in an empty post-submit
// portal. Mark hit this 2026-06-24 with the Commercial application.
//
// The labels below are read-only display only — they match the labels
// returned by /api/loan-types. Kept here as a static map so we don't
// need an extra fetch just to render the badge.
const LOAN_TYPE_LABEL_MAP = {
  commercial_loan: "Commercial",
  residential_loan: "Residential",
  residential_investment_loan: "Residential Investment",
  bridging_loan: "Bridging",
  business_loan: "Business Loan",
  construction_finance: "Construction Finance",
  residual_stock_loan: "Residual Stock Loan",
  mezzanine_loan: "Mezzanine",
  equity_loan: "Equity",
  industrial_loan: "Industrial",
  agricultural_loan: "Agricultural",
  // Legacy ids preserved as fallback labels so old drafts in the DB
  // still render something readable while Mark migrates them.
  commercial_property_loan: "Commercial Property (legacy)",
  development_construction_loan: "Development / Construction (legacy)",
  land_bank_land_loan: "Land Bank (legacy)",
  short_term_bridging_loan: "Short Term Bridging (legacy)",
  other: "Other (legacy)",
};

const LOAN_PURPOSES = [
  "Purchase",
  "Seed Capital",
  "Equity Venture",
  "Cash Out",
  "Refinance",
  "Construction",
  "Settlement Shortfall",
  "Payout Existing Debt",
  "Other",
];

const EXIT_STRATEGIES = [
  "Finance",
  "Sale of Security",
  "Cash Flow",
  "Other",
];

const emptyRequirement = {
  description: "",
  amount: 0,
};

export const StepLoanDetails = ({ formData, updateFormData }) => {
  const [loanDetails, setLoanDetails] = useState(formData.loan_details);
  const [requirements, setRequirements] = useState(
    formData.loan_requirements.length > 0 
      ? formData.loan_requirements 
      : [{ ...emptyRequirement }]
  );
  const [exitStrategy, setExitStrategy] = useState(formData.exit_strategy);
  const [settlementDate, setSettlementDate] = useState(
    formData.loan_details.proposed_settlement_date 
      ? new Date(formData.loan_details.proposed_settlement_date) 
      : null
  );

  useEffect(() => {
    updateFormData("loan_details", loanDetails);
  }, [loanDetails, updateFormData]);

  useEffect(() => {
    updateFormData("loan_requirements", requirements);
  }, [requirements, updateFormData]);

  useEffect(() => {
    updateFormData("exit_strategy", exitStrategy);
  }, [exitStrategy, updateFormData]);

  const handleLoanChange = (field, value) => {
    setLoanDetails({ ...loanDetails, [field]: value });
  };

  const handlePurposeChange = (purpose, checked) => {
    let newPurposes = [...(loanDetails.loan_purposes || [])];
    if (checked) {
      if (!newPurposes.includes(purpose)) {
        newPurposes.push(purpose);
      }
    } else {
      newPurposes = newPurposes.filter((p) => p !== purpose);
    }
    handleLoanChange("loan_purposes", newPurposes);
  };

  const handleDateChange = (date) => {
    setSettlementDate(date);
    handleLoanChange("proposed_settlement_date", date ? format(date, "yyyy-MM-dd") : "");
  };

  const handleRequirementChange = (index, field, value) => {
    const newRequirements = [...requirements];
    newRequirements[index] = { ...newRequirements[index], [field]: value };
    setRequirements(newRequirements);
  };

  const addRequirement = () => {
    setRequirements([...requirements, { ...emptyRequirement }]);
  };

  const removeRequirement = (index) => {
    if (requirements.length > 1) {
      setRequirements(requirements.filter((_, i) => i !== index));
    }
  };

  const handleExitStrategyChange = (method, checked) => {
    let newMethods = [...(exitStrategy.methods || [])];
    if (checked) {
      if (!newMethods.includes(method)) {
        newMethods.push(method);
      }
    } else {
      newMethods = newMethods.filter((m) => m !== method);
    }
    setExitStrategy({ ...exitStrategy, methods: newMethods });
  };

  const totalRequirements = requirements.reduce(
    (sum, req) => sum + (parseFloat(req.amount) || 0),
    0
  );

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 2,
    }).format(value);
  };

  return (
    <div className="space-y-10">
      {/* HF-Apply-10 (2026-06-24) — read-only loan type. Selection
          happens on Step 2 (Loan Type). Showing it here as a badge so
          the borrower has context for the amount/term they're about
          to fill in, with a clear nudge if they need to change it.
          The OLD dropdown was overwriting Step 2 with legacy ids and
          breaking checklist auto-creation on submit. */}
      <section className="form-card rounded">
        <h3 className="section-subheader">Loan Type</h3>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-gray-500">Selected on Step 2:</span>
          <span
            className="inline-flex items-center px-3 py-1.5 rounded-md border border-[#bd9a59]/40 bg-[#bd9a59]/10 text-[#021d49] font-medium text-sm"
            data-testid="loan-type-readonly"
          >
            {LOAN_TYPE_LABEL_MAP[loanDetails.loan_type] || loanDetails.loan_type || "Not selected"}
          </span>
          <span className="text-xs text-gray-400 italic">
            To change, click "Loan Type" in the sidebar.
          </span>
        </div>
      </section>

      {/* Loan Details Section */}
      <section className="form-card rounded">
        <h3 className="section-subheader">Loan Details</h3>
        
        <div className="form-grid form-grid-2">
          <div>
            <Label className="form-label form-label-required">Net Loan Required ($)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <Input
                type="number"
                value={loanDetails.net_loan_required || ""}
                onChange={(e) => handleLoanChange("net_loan_required", parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                className="form-input pl-8 text-right"
                data-testid="loan-amount-input"
              />
            </div>
          </div>
          
          <div>
            <Label className="form-label form-label-required">Term Required (Months)</Label>
            <Input
              type="number"
              value={loanDetails.term_months || ""}
              onChange={(e) => handleLoanChange("term_months", parseInt(e.target.value) || 0)}
              placeholder="e.g., 12"
              className="form-input"
              data-testid="loan-term-input"
            />
          </div>
          
          <div>
            <Label className="form-label">Proposed Settlement Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal form-input"
                  data-testid="settlement-date-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {settlementDate ? format(settlementDate, "PPP") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                {/* PR HF-Apply-13 (2026-06-26) — disable past dates. Borrowers
                    were entering settlement dates in the past and the form
                    silently accepted them, then downstream commission math
                    treated those deals as already-settled and skewed
                    projections. */}
                <Calendar
                  mode="single"
                  selected={settlementDate}
                  onSelect={handleDateChange}
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date < today;
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          
          <div>
            <Label className="form-label">Expected Rate (% p.a)</Label>
            <Input
              value={loanDetails.expected_rate}
              onChange={(e) => handleLoanChange("expected_rate", e.target.value)}
              placeholder="e.g., 12.5"
              className="form-input"
              data-testid="expected-rate-input"
            />
          </div>
        </div>
      </section>

      {/* Loan Purpose Section */}
      <section className="form-card rounded">
        <h3 className="section-subheader">Loan Purpose</h3>
        <p className="text-sm text-gray-500 mb-4">Select all that apply</p>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {LOAN_PURPOSES.map((purpose) => (
            <div key={purpose} className="flex items-center gap-2">
              <Checkbox
                id={`purpose-${purpose}`}
                checked={(loanDetails.loan_purposes || []).includes(purpose)}
                onCheckedChange={(checked) => handlePurposeChange(purpose, checked)}
                data-testid={`loan-purpose-${purpose.toLowerCase().replace(' ', '-')}`}
              />
              <Label htmlFor={`purpose-${purpose}`} className="cursor-pointer text-sm">
                {purpose}
              </Label>
            </div>
          ))}
        </div>
        
        {(loanDetails.loan_purposes || []).includes("Other") && (
          <div className="mt-4">
            <Label className="form-label">Please specify other purpose</Label>
            <Input
              value={loanDetails.loan_purpose_other || ""}
              onChange={(e) => handleLoanChange("loan_purpose_other", e.target.value)}
              placeholder="Describe the loan purpose"
              className="form-input max-w-md"
              data-testid="loan-purpose-other-input"
            />
          </div>
        )}

        <div className="mt-6">
          <Label className="form-label">Additional Comments</Label>
          <Textarea
            value={loanDetails.additional_comments || ""}
            onChange={(e) => handleLoanChange("additional_comments", e.target.value)}
            placeholder="Any additional information about the loan purpose..."
            className="form-input min-h-[100px]"
            data-testid="loan-comments-textarea"
          />
        </div>
      </section>

      {/* Other Credit Providers */}
      <section className="form-card rounded">
        <h3 className="section-subheader">Other Credit Providers</h3>
        
        <div className="mb-4">
          <Label className="form-label mb-3">
            Has any application in respect to this loan been submitted by you or any other person to other credit providers?
          </Label>
          <RadioGroup
            value={loanDetails.submitted_to_other_providers ? "yes" : "no"}
            onValueChange={(v) => handleLoanChange("submitted_to_other_providers", v === "yes")}
            className="flex gap-6"
            data-testid="other-providers-radio"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="yes" id="other-prov-yes" />
              <Label htmlFor="other-prov-yes" className="cursor-pointer">Yes</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="no" id="other-prov-no" />
              <Label htmlFor="other-prov-no" className="cursor-pointer">No</Label>
            </div>
          </RadioGroup>
        </div>
        
        {loanDetails.submitted_to_other_providers && (
          <div>
            <Label className="form-label">Please provide details</Label>
            <Textarea
              value={loanDetails.other_providers_details || ""}
              onChange={(e) => handleLoanChange("other_providers_details", e.target.value)}
              placeholder="List the credit providers and outcomes..."
              className="form-input min-h-[80px]"
              data-testid="other-providers-details-textarea"
            />
          </div>
        )}
      </section>

      {/* Loan Requirements Breakdown */}
      <section className="form-card rounded">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="section-subheader mb-0">Loan Requirements</h3>
            <p className="text-sm text-gray-500">Breakdown of how the funds will be used</p>
          </div>
          <button
            onClick={addRequirement}
            className="btn-secondary px-3 py-2 rounded text-sm flex items-center gap-1"
            data-testid="add-requirement-btn"
          >
            <Plus className="w-4 h-4" />
            Add Item
          </button>
        </div>

        <div className="space-y-3">
          {requirements.map((req, index) => (
            <div 
              key={index} 
              className="flex flex-col md:flex-row gap-3 p-3 bg-gray-50 rounded"
              data-testid={`requirement-row-${index}`}
            >
              <div className="flex-1">
                <Label className="form-label text-xs md:hidden">Description of Funds Required</Label>
                <Input
                  value={req.description}
                  onChange={(e) => handleRequirementChange(index, "description", e.target.value)}
                  placeholder="Description of funds required"
                  className="form-input"
                  data-testid={`requirement-desc-${index}`}
                />
              </div>
              <div className="w-full md:w-40">
                <Label className="form-label text-xs md:hidden">Amount ($)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    type="number"
                    value={req.amount || ""}
                    onChange={(e) => handleRequirementChange(index, "amount", parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    className="form-input pl-8 text-right"
                    data-testid={`requirement-amount-${index}`}
                  />
                </div>
              </div>
              {requirements.length > 1 && (
                <button
                  onClick={() => removeRequirement(index)}
                  className="text-red-500 hover:text-red-700 p-2 self-center"
                  data-testid={`remove-requirement-${index}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Total */}
        <div className="mt-4 p-4 bg-[#021d49] text-white rounded flex justify-between items-center">
          <span className="font-medium">Total Funds Required:</span>
          <span className="text-xl font-bold text-[#bd9a59]" data-testid="requirements-total">
            {formatCurrency(totalRequirements)}
          </span>
        </div>

        {loanDetails.net_loan_required > 0 && Math.abs(totalRequirements - loanDetails.net_loan_required) > 0.01 && (
          <p className="text-sm text-yellow-600 mt-2">
            Note: Total funds required ({formatCurrency(totalRequirements)}) does not match net loan required ({formatCurrency(loanDetails.net_loan_required)})
          </p>
        )}
      </section>

      {/* Proposed Exit Strategy */}
      <section className="form-card rounded">
        <h3 className="section-subheader">Proposed Exit Strategy</h3>
        <p className="text-sm text-gray-500 mb-4">
          How do you intend to repay or exit this loan? Select all that apply.
        </p>
        
        <div className="flex flex-wrap gap-4 mb-4">
          {EXIT_STRATEGIES.map((method) => (
            <div key={method} className="flex items-center gap-2">
              <Checkbox
                id={`exit-${method}`}
                checked={(exitStrategy.methods || []).includes(method)}
                onCheckedChange={(checked) => handleExitStrategyChange(method, checked)}
                data-testid={`exit-strategy-${method.toLowerCase().replace(' ', '-')}`}
              />
              <Label htmlFor={`exit-${method}`} className="cursor-pointer text-sm">
                {method}
              </Label>
            </div>
          ))}
        </div>
        
        {(exitStrategy.methods || []).includes("Other") && (
          <div>
            <Label className="form-label">Please provide details</Label>
            <Textarea
              value={exitStrategy.other_details || ""}
              onChange={(e) => setExitStrategy({ ...exitStrategy, other_details: e.target.value })}
              placeholder="Describe your exit strategy..."
              className="form-input min-h-[80px]"
              data-testid="exit-strategy-other-textarea"
            />
          </div>
        )}

        <div className="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-500 rounded">
          <p className="text-sm text-yellow-800">
            Please attach copies of supporting documents for your Finance Takeout Method in the Documents section (Step 9).
          </p>
        </div>
      </section>
    </div>
  );
};
